<template>
  <div class="kv-ve-bg">
    <div class="kv-ve-bg__header">
      <label class="kv-ve-bg__label">{{ label }}</label>
      <div class="kv-ve-bg__mode">
        <button
          type="button"
          class="kv-ve-bg__mode-btn"
          :class="{ active: safe.mode === 'color' }"
          :aria-pressed="safe.mode === 'color' ? 'true' : 'false'"
          @click="setMode('color')"
        >
          {{ resolvedColorLabel }}
        </button>
        <button
          type="button"
          class="kv-ve-bg__mode-btn"
          :class="{ active: safe.mode === 'image' }"
          :aria-pressed="safe.mode === 'image' ? 'true' : 'false'"
          @click="setMode('image')"
        >
          {{ resolvedImageLabel }}
        </button>
      </div>
    </div>

    <div v-if="safe.mode === 'color'" class="kv-ve-bg__body">
      <VeColorPicker v-model="colorProxy" />
    </div>

      <div v-else class="kv-ve-bg__body">
      <div class="kv-ve-bg__image">
        <div
          class="kv-ve-bg__image-preview"
          :class="{ empty: !safe.imageUrl }"
          :style="safe.imageUrl ? previewStyle : null"
          @dblclick="safe.imageUrl ? null : (modalOpen = true)"
        >
          <img
            v-if="safe.imageUrl && safe.repeat === 'no-repeat'"
            :src="safe.imageUrl"
            alt=""
            @dblclick.stop="modalOpen = true"
          />
          <div v-else class="kv-ve-bg__image-empty">{{ resolvedEmptyImageLabel }}</div>
        </div>
        <div class="kv-ve-bg__image-actions">
          <button type="button" class="btn btn-sm btn-outline-secondary" @click="modalOpen = true">
            {{ resolvedChooseImageLabel }}
          </button>
          <button
            v-if="safe.imageUrl"
            type="button"
            class="btn btn-sm btn-outline-danger"
            @click="setImage(null)"
          >
            {{ resolvedRemoveImageLabel }}
          </button>
        </div>
      </div>

      <div class="kv-ve-bg__controls">
        <div class="form-group">
          <label class="kv-ve-bg__sub-label">{{ $_('visualEditor-emailEditor-backgroundPicker-size') }}</label>
          <select class="form-control" :value="safe.sizeMode" @change="setSizeMode($event.target.value)">
            <option value="cover">{{ $_('visualEditor-emailEditor-backgroundPicker-sizeCover') }}</option>
            <option value="contain">{{ $_('visualEditor-emailEditor-backgroundPicker-sizeContain') }}</option>
            <option value="custom">{{ $_('visualEditor-emailEditor-backgroundPicker-sizeCustomWidth') }}</option>
          </select>
        </div>

        <div v-if="safe.sizeMode === 'custom'" class="form-group">
          <div style="display:flex; align-items:center; justify-content:space-between; gap:10px; margin-bottom:8px;">
            <div style="display:flex; align-items:baseline; gap:6px;">
              <label class="kv-ve-bg__sub-label" style="margin:0;">{{ $_('visualEditor-emailEditor-common-width') }}</label>
              <VeWidthUnitToggle v-model="sizeUnitProxy" :label="$_('visualEditor-emailEditor-common-width')" pctTitle="%" pxTitle="px" />
            </div>
            <div style="display:flex; align-items:center; gap:8px;">
              <input
                v-if="sizeUnitProxy === 'pct'"
                class="form-control"
                type="number"
                v-model.number="sizePctProxy"
                min="0"
                max="100"
                style="width: 88px; height: 34px; padding: 6px 10px; font-size: 12px; font-variant-numeric: tabular-nums;"
              />
              <input
                v-else
                class="form-control"
                type="number"
                v-model.number="sizePxProxy"
                min="100"
                max="2000"
                style="width: 88px; height: 34px; padding: 6px 10px; font-size: 12px; font-variant-numeric: tabular-nums;"
              />
              <div style="font-size:12px; font-variant-numeric: tabular-nums; color:#667085;">
                {{ sizeUnitProxy === 'pct' ? '%' : 'px' }}
              </div>
            </div>
          </div>

          <VeWidthSlider
            v-if="sizeUnitProxy === 'pct'"
            v-model="sizePctProxy"
            :label="$_('visualEditor-emailEditor-common-width')"
            :min="0"
            :max="100"
            :step="1"
            unit="%"
            :showHeader="false"
          />
          <VeWidthSlider
            v-else
            v-model="sizePxProxy"
            :label="$_('visualEditor-emailEditor-common-widthPx')"
            :min="100"
            :max="2000"
            :step="10"
            unit="px"
            :showHeader="false"
          />
        </div>

        <div class="form-row">
          <div class="form-group col-6">
            <label class="kv-ve-bg__sub-label">{{ $_('visualEditor-emailEditor-backgroundPicker-repeat') }}</label>
            <select class="form-control" :value="safe.repeat" @change="setRepeat($event.target.value)">
              <option value="no-repeat">{{ $_('visualEditor-emailEditor-backgroundPicker-repeatNoRepeat') }}</option>
              <option value="repeat">{{ $_('visualEditor-emailEditor-backgroundPicker-repeatRepeat') }}</option>
              <option value="repeat-x">{{ $_('visualEditor-emailEditor-backgroundPicker-repeatRepeatX') }}</option>
              <option value="repeat-y">{{ $_('visualEditor-emailEditor-backgroundPicker-repeatRepeatY') }}</option>
            </select>
          </div>
          <div class="form-group col-6">
            <label class="kv-ve-bg__sub-label">{{ $_('visualEditor-emailEditor-backgroundPicker-position') }}</label>
            <select class="form-control" :value="safe.position" @change="setPosition($event.target.value)">
              <option value="left top">{{ $_('visualEditor-emailEditor-backgroundPicker-positionLeftTop') }}</option>
              <option value="center top">{{ $_('visualEditor-emailEditor-backgroundPicker-positionCenterTop') }}</option>
              <option value="right top">{{ $_('visualEditor-emailEditor-backgroundPicker-positionRightTop') }}</option>
              <option value="left center">{{ $_('visualEditor-emailEditor-backgroundPicker-positionLeftCenter') }}</option>
              <option value="center center">{{ $_('visualEditor-emailEditor-backgroundPicker-positionCenterCenter') }}</option>
              <option value="right center">{{ $_('visualEditor-emailEditor-backgroundPicker-positionRightCenter') }}</option>
              <option value="left bottom">{{ $_('visualEditor-emailEditor-backgroundPicker-positionLeftBottom') }}</option>
              <option value="center bottom">{{ $_('visualEditor-emailEditor-backgroundPicker-positionCenterBottom') }}</option>
              <option value="right bottom">{{ $_('visualEditor-emailEditor-backgroundPicker-positionRightBottom') }}</option>
            </select>
          </div>
        </div>
      </div>

      <FilesGalleryModal
        v-model="modalOpen"
        size="large"
        :selectable="true"
        :autoCloseOnSelect="true"
        :allowUpload="true"
        :allowDelete="false"
        type-filter="image"
        @select="onPickFile"
      />
    </div>
  </div>
</template>

<script>
import FilesGalleryModal from "@/files-gallery/files-gallery-modal.vue";
import VeColorPicker from "./VeColorPicker.vue";
import VeWidthUnitToggle from "./VeWidthUnitToggle.vue";
import VeWidthSlider from "./VeWidthSlider.vue";

export default {
  name: "VeBackgroundPicker",
  components: { FilesGalleryModal, VeColorPicker, VeWidthUnitToggle, VeWidthSlider },
  model: { prop: "value", event: "input" },
  props: {
    label: { type: String, default: "" },
    value: {
      type: Object,
      default: () => ({
        mode: "color",
        color: "#ffffff",
        imageUrl: null,
        sizeMode: "cover",
        sizeUnit: "pct",
        sizePx: 600,
        sizePct: 100,
        repeat: "no-repeat",
        position: "center center",
      }),
    },
    colorLabel: { type: String, default: "" },
    imageLabel: { type: String, default: "" },
    chooseImageLabel: { type: String, default: "" },
    removeImageLabel: { type: String, default: "" },
    emptyImageLabel: { type: String, default: "" },
  },
  data() {
    return { modalOpen: false };
  },
  computed: {
    resolvedColorLabel() {
      return this.colorLabel || this.$_("visualEditor-emailEditor-backgroundPicker-color");
    },
    resolvedImageLabel() {
      return this.imageLabel || this.$_("visualEditor-emailEditor-backgroundPicker-image");
    },
    resolvedChooseImageLabel() {
      return this.chooseImageLabel || this.$_("visualEditor-emailEditor-backgroundPicker-choose");
    },
    resolvedRemoveImageLabel() {
      return this.removeImageLabel || this.$_("visualEditor-emailEditor-backgroundPicker-remove");
    },
    resolvedEmptyImageLabel() {
      return this.emptyImageLabel || this.$_("visualEditor-emailEditor-backgroundPicker-empty");
    },
    safe() {
      const v = this.value || {};
      const mode = String(v.mode || "color").toLowerCase() === "image" ? "image" : "color";
      const color = typeof v.color === "string" && v.color ? v.color : "#ffffff";
      const imageUrl = typeof v.imageUrl === "string" && v.imageUrl ? v.imageUrl : null;

      const sizeModeRaw = String(v.sizeMode || "").toLowerCase();
      const sizeMode = sizeModeRaw === "contain" || sizeModeRaw === "custom" ? sizeModeRaw : "cover";

      const sizeUnitRaw = String(v.sizeUnit || "").toLowerCase();
      const sizeUnit = sizeUnitRaw === "px" ? "px" : "pct";

      const px = Number(v.sizePx);
      const sizePx = Number.isFinite(px) ? Math.min(2000, Math.max(100, Math.round(px))) : 600;
      const pct = Number(v.sizePct);
      const sizePct = Number.isFinite(pct) ? Math.min(100, Math.max(0, Math.round(pct))) : 100;

      const repeatRaw = String(v.repeat || "").toLowerCase();
      const repeat =
        repeatRaw === "repeat" || repeatRaw === "repeat-x" || repeatRaw === "repeat-y"
          ? repeatRaw
          : "no-repeat";

      const positionRaw = String(v.position || "").toLowerCase();
      const allowedPositions = new Set([
        "left top",
        "center top",
        "right top",
        "left center",
        "center center",
        "right center",
        "left bottom",
        "center bottom",
        "right bottom",
        "center",
      ]);
      const position = allowedPositions.has(positionRaw) ? (positionRaw === "center" ? "center center" : positionRaw) : "center center";

      return { mode, color, imageUrl, sizeMode, sizeUnit, sizePx, sizePct, repeat, position };
    },
    colorProxy: {
      get() {
        return this.safe.color;
      },
      set(next) {
        this.$emit("input", { ...this.safe, mode: "color", color: next });
      },
    },
    sizeUnitProxy: {
      get() {
        return this.safe.sizeUnit;
      },
      set(next) {
        const u = String(next || "").toLowerCase() === "px" ? "px" : "pct";
        this.$emit("input", { ...this.safe, mode: "image", sizeUnit: u, sizeMode: "custom" });
      },
    },
    sizePxProxy: {
      get() {
        return this.safe.sizePx;
      },
      set(next) {
        const n = Number(next);
        const v = Number.isFinite(n) ? Math.min(2000, Math.max(100, Math.round(n))) : 600;
        this.$emit("input", { ...this.safe, mode: "image", sizeMode: "custom", sizePx: v });
      },
    },
    sizePctProxy: {
      get() {
        return this.safe.sizePct;
      },
      set(next) {
        const n = Number(next);
        const v = Number.isFinite(n) ? Math.min(100, Math.max(0, Math.round(n))) : 100;
        this.$emit("input", { ...this.safe, mode: "image", sizeMode: "custom", sizePct: v });
      },
    },
    previewStyle() {
      if (!this.safe.imageUrl) return {};
      const base = {
        backgroundImage: `url("${this.safe.imageUrl}")`,
        backgroundRepeat: this.safe.repeat,
        backgroundPosition: this.safe.position,
      };
      if (this.safe.sizeMode === "cover" || this.safe.sizeMode === "contain") {
        return { ...base, backgroundSize: this.safe.sizeMode };
      }
      const size = this.safe.sizeUnit === "px" ? `${this.safe.sizePx}px auto` : `${this.safe.sizePct}% auto`;
      return { ...base, backgroundSize: size };
    },
  },
  methods: {
    setMode(mode) {
      const m = mode === "image" ? "image" : "color";
      this.$emit("input", { ...this.safe, mode: m });
    },
    setImage(url) {
      this.$emit("input", { ...this.safe, mode: "image", imageUrl: url });
    },
    setSizeMode(mode) {
      const m = String(mode || "").toLowerCase();
      const safe = m === "contain" || m === "custom" ? m : "cover";
      this.$emit("input", { ...this.safe, mode: "image", sizeMode: safe });
    },
    setRepeat(v) {
      const r = String(v || "").toLowerCase();
      const safe = r === "repeat" || r === "repeat-x" || r === "repeat-y" ? r : "no-repeat";
      this.$emit("input", { ...this.safe, mode: "image", repeat: safe });
    },
    setPosition(v) {
      const p = String(v || "").toLowerCase();
      this.$emit("input", { ...this.safe, mode: "image", position: p || "center center" });
    },
    onPickFile(file) {
      if (!file || !file.url) return;
      this.setImage(file.url);
      this.modalOpen = false;
    },
  },
};
</script>

<style scoped lang="less">
.kv-ve-bg__header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 8px;
}
.kv-ve-bg__label {
  margin: 0;
  font-size: 12px;
  font-weight: 600;
  color: #344054;
}
.kv-ve-bg__mode {
  display: inline-flex;
  gap: 8px;
}
.kv-ve-bg__mode-btn {
  all: unset;
  cursor: pointer;
  font-size: 12px;
  font-weight: 400;
  color: #667085;
  border-bottom: 1px solid transparent;
  padding: 0 2px;
}
.kv-ve-bg__mode-btn.active {
  color: #101828;
  border-color: rgba(16, 24, 40, 0.55);
}
.kv-ve-bg__image {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.kv-ve-bg__image-preview {
  width: 100%;
  height: 120px;
  border: 1px dashed #d0d5dd;
  border-radius: 12px;
  overflow: hidden;
  background: #f9fafb;
  display: flex;
  align-items: center;
  justify-content: center;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  cursor: pointer;
}
.kv-ve-bg__image-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  cursor: pointer;
}
.kv-ve-bg__image-empty {
  font-size: 13px;
  color: #667085;
}
.kv-ve-bg__image-actions {
  display: flex;
  gap: 8px;
}
.kv-ve-bg__controls {
  margin-top: 10px;
}
.kv-ve-bg__sub-label {
  font-size: 12px;
  font-weight: 600;
  color: #344054;
  margin-bottom: 6px;
}
</style>



